import { makeAutoObservable } from "mobx";

const STORAGE_KEY = "hp_listening_progress";
const MAX_ENTRIES = 100;
const MAX_AGE_MS = 180 * 24 * 60 * 60 * 1000;
/** Positions shorter than this are not worth resuming */
const MIN_RESUME_SECONDS = 10;
/** An episode counts as played when this much of it is done... */
const PLAYED_FRACTION = 0.95;
/** ...or when only this many seconds remain */
const PLAYED_REMAINING_SECONDS = 30;

type EpisodeSnapshot = Pick<
    Episode,
    "id" | "guid" | "title" | "slug" | "sound" | "published" | "length" | "duration" | "podcast_id" | "art"
>;
type PodcastSnapshot = Pick<Podcast, "id" | "name" | "slug" | "art" | "blurhash">;

export interface ListeningEntry {
    /** Seconds into the episode */
    position: number;
    /** Seconds; 0 when unknown */
    duration: number;
    played: boolean;
    updatedAt: number;
    episode: EpisodeSnapshot;
    podcast: PodcastSnapshot;
}

const isEntry = (value: unknown): value is ListeningEntry => {
    const entry = value as ListeningEntry | null;
    return (
        !!entry &&
        typeof entry.position === "number" &&
        typeof entry.duration === "number" &&
        typeof entry.updatedAt === "number" &&
        !!entry.episode &&
        !!entry.podcast
    );
};

const isDone = (position: number, duration: number) =>
    duration > 0 && (position / duration >= PLAYED_FRACTION || duration - position <= PLAYED_REMAINING_SECONDS);

/**
 * Where the listener is in every episode they have started, kept on this device.
 * Powers resuming, the progress bars and the "Fortsätt lyssna" shelf.
 */
export class ListeningStore {
    entries: Record<string, ListeningEntry> = {};

    constructor() {
        makeAutoObservable(this);
        this.load();
    }

    get inProgress(): ListeningEntry[] {
        return Object.values(this.entries)
            .filter((entry) => !entry.played && entry.position >= MIN_RESUME_SECONDS)
            .sort((a, b) => b.updatedAt - a.updatedAt);
    }

    /** 0..1 for an episode that has been started and not finished, otherwise 0 */
    progress(episodeId: number): number {
        const entry = this.entries[episodeId];
        if (!entry || entry.played || entry.duration <= 0) return 0;
        return Math.min(1, entry.position / entry.duration);
    }

    entry(episodeId: number): ListeningEntry | undefined {
        return this.entries[episodeId];
    }

    isPlayed(episodeId: number): boolean {
        return this.entries[episodeId]?.played === true;
    }

    /** Where to start an episode: 0 for new or finished ones */
    resumePosition(episodeId: number): number {
        const entry = this.entries[episodeId];
        if (!entry || entry.played || entry.position < MIN_RESUME_SECONDS) return 0;
        return Math.floor(entry.position);
    }

    record(episode: Episode, podcast: Podcast, position: number, duration: number) {
        if (!Number.isFinite(position) || position < 0 || episode.id < 0) return;

        const knownDuration =
            Number.isFinite(duration) && duration > 0 ? duration : this.entries[episode.id]?.duration || 0;
        this.entries[episode.id] = {
            position,
            duration: knownDuration,
            played: isDone(position, knownDuration),
            updatedAt: Date.now(),
            episode: this.snapshotEpisode(episode),
            podcast: this.snapshotPodcast(podcast),
        };
        this.save();
    }

    markPlayed(episode: Episode, podcast: Podcast) {
        const existing = this.entries[episode.id];
        this.entries[episode.id] = {
            position: existing?.duration || existing?.position || 0,
            duration: existing?.duration || 0,
            played: true,
            updatedAt: Date.now(),
            episode: this.snapshotEpisode(episode),
            podcast: this.snapshotPodcast(podcast),
        };
        this.save();
    }

    markUnplayed(episodeId: number) {
        delete this.entries[episodeId];
        this.save();
    }

    clear() {
        this.entries = {};
        this.save();
    }

    private snapshotEpisode(episode: Episode): EpisodeSnapshot {
        const { id, guid, title, slug, sound, published, length, duration, podcast_id, art } = episode;
        return { id, guid, title, slug, sound, published, length, duration, podcast_id, art };
    }

    private snapshotPodcast(podcast: Podcast): PodcastSnapshot {
        const { id, name, slug, art, blurhash } = podcast;
        return { id, name, slug, art, blurhash };
    }

    private load() {
        try {
            const raw = localStorage.getItem(STORAGE_KEY);
            if (!raw) return;
            const parsed: unknown = JSON.parse(raw);
            if (!parsed || typeof parsed !== "object") return;

            const cutoff = Date.now() - MAX_AGE_MS;
            for (const [id, value] of Object.entries(parsed)) {
                if (isEntry(value) && value.updatedAt >= cutoff) {
                    this.entries[id] = value;
                }
            }
        } catch {
            // Corrupt or blocked storage: start empty
        }
    }

    private save() {
        const newest = Object.entries(this.entries)
            .sort(([, a], [, b]) => b.updatedAt - a.updatedAt)
            .slice(0, MAX_ENTRIES);
        this.entries = Object.fromEntries(newest);

        try {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(this.entries));
        } catch {
            // Blocked or full: keep working in memory
        }
    }
}
